import { InvitationDialog } from '#components'
import {
	DialogController,
	PredefinedDialog,
	PredefinedDialogType,
} from '~/components/passporterUi/Dialog/dialogController'
import { Itinerary2 } from '~/models/Itinerary'
import { Traveler2, TravelerRole } from '~/models/Traveler'
import { ItineraryService } from '~/passporter-services/itinerary/service'
import CookieService from '~/services/cookies'
import EventsService from '~/services/events'
import { AuthNavigation } from '~/services/navigation/authNavigation'
import { HomeNavigation } from '~/services/navigation/home'
import { TripNavigation } from '~/services/navigation/trip'
import { TranslationService } from '~/services/Translation'
import { InvitationService } from '~/services/trip/invitation/service'
import { useItineraryStore } from '~/store/itinerary'
import { useProfileTrips } from '~/store/profileTrips'

export class TripTravelerActions {
	static async getTravelers(itineraryId: string): Promise<Traveler2[]> {
		try {
			return await ItineraryService.getTravelers(itineraryId)
		} catch {
			return []
		}
	}

	static async deleteTraveler(itineraryId: string, travelerId: string): Promise<void> {
		const translation = TranslationService.getInstance()
		try {
			await ItineraryService.deleteTraveler(itineraryId, travelerId)
			this.profileTripsStore.resetAll()
			EventsService.emitSuccess(
				translation.translate('Toast_itinerary_deleted_traveller_from_itinerary')
			)
		} catch {
			EventsService.emitError(translation.translate('Common_error'))
		}
	}

	static async canEdit(itineraryId: string, profileId?: string): Promise<boolean> {
		if (!profileId) return false
		try {
			const travelers = await this.getTravelers(itineraryId)
			return ItineraryService.canEdit(travelers, profileId)
		} catch {
			return false
		}
	}

	static async createInvitationDialog(
		itinerary: Itinerary2,
		isXS: boolean
	): Promise<DialogController> {
		const invitationDialog = new PredefinedDialog(PredefinedDialogType.EMPTY, InvitationDialog)
			.size('s')
			.props({ itinerary, isXS })
			.mobileType('fullScreen')
		return new DialogController(invitationDialog.build())
	}

	static async generateInvitationLink(itineraryId: string): Promise<string> {
		try {
			const role: TravelerRole = 'manager'
			const invitation = await InvitationService.generateInvitationCode(itineraryId, role)
			return invitation.url!
		} catch {
			return ''
		}
	}

	static async copyInvitationLinkToClipboard(itineraryId: string): Promise<void> {
		const invitationLink: string = await this.generateInvitationLink(itineraryId)
		await navigator.clipboard.writeText(invitationLink)
		EventsService.emitSuccess(
			TranslationService.getInstance().translate('Toast_profile_link_copied_to_clipboard')
		)
	}

	static async acceptInvitation(
		tripId: string,
		code: string,
		goHomeOnError?: boolean
	): Promise<void> {
		const t = TranslationService.getInstance()
		try {
			await InvitationService.acceptInvitation(tripId, code)
			EventsService.emitSuccess(t.translate('web.trip.join.toast.success'))
			this.profileTripsStore.resetAll()
			TripNavigation.toTripDashboard(tripId)
		} catch {
			if (goHomeOnError) {
				HomeNavigation.toHome()
				return
			}
			EventsService.emitError(t.translate('web.trip.join.toast.error'))
		}
	}

	static saveInvitation(tripId: string, code: string) {
		CookieService.setTripInvitation({ tripId, code })
		return AuthNavigation.toLogin()
	}

	private static get itineraryStore() {
		return useItineraryStore()
	}

	private static get profileTripsStore() {
		return useProfileTrips()
	}
}
